import { type ReactNode, useEffect, useState } from 'react';
import { useSearch } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { staffBookingSettingsService } from '@/services/api/availability.service';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Calendar, User, RotateCcw } from 'lucide-react';
import { ProfileSettingsPanel } from '@/components/settings/ProfileSettingsPanel';

const BUFFER_OPTIONS = [0, 5, 10, 15, 30];
const INTERVAL_OPTIONS = [15, 30, 45, 60];

// Mirrors the admin settings layout: a left-aligned section title/description
// column with the form fields on the right.
function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-4 py-8 first:pt-0">
      <div className="lg:col-span-1">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description && (
          <p className="text-body-xs text-soft mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="lg:col-span-2 space-y-4">{children}</div>
    </section>
  );
}

export default function StaffSettingsPage() {
  const { t } = useTranslation(['staff', 'common', 'settings', 'calendar']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const search = useSearch();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(search).get('tab') || 'booking',
  );

  // Sync the active tab when the ?tab= query changes (e.g. deep link from the
  // sidebar profile link while already on the settings page).
  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) setActiveTab(tab);
  }, [search]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Local text state so the number inputs can be cleared mid-edit; the saved
  // value is mutated only on blur (not on every keystroke).
  const [advanceText, setAdvanceText] = useState('');
  const [noticeText, setNoticeText] = useState('');

  useSetPageHeader(
    {
      title: t('staff:settings.title', 'Settings'),
      sub: t('staff:settings.subtitle', 'Configure your booking preferences'),
    },
    [t],
  );

  // Query
  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: ['staff-booking-settings'],
    queryFn: () => staffBookingSettingsService.getSettings()
  });

  // Mutations
  const updateSettingsMutation = useMutation({
    mutationFn: staffBookingSettingsService.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-booking-settings'] });
      toast({ title: t('common:toasts.updateSuccess') });
    },
    onError: () => {
      toast({ title: t('common:toasts.saveFailed'), variant: 'destructive' });
    }
  });

  const resetSettingsMutation = useMutation({
    mutationFn: staffBookingSettingsService.resetSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-booking-settings'] });
      toast({ title: t('staff:settings.resetSuccess', 'Settings reset to defaults') });
    },
    onError: () => {
      toast({ title: t('common:toasts.saveFailed'), variant: 'destructive' });
    }
  });

  useEffect(() => {
    if (settings) {
      setAdvanceText(String(settings.advance_booking_days ?? 60));
      setNoticeText(String(settings.minimum_notice_hours ?? 2));
    }
  }, [settings]);

  // Debounced auto-save for advance booking days when text differs from saved value.
  useEffect(() => {
    if (!settings) return;
    const n = parseInt(advanceText, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(1, Math.min(365, n));
    if (clamped === settings.advance_booking_days) return;
    const id = setTimeout(() => {
      updateSettingsMutation.mutate({ advance_booking_days: clamped });
    }, 400);
    return () => clearTimeout(id);
  }, [advanceText, settings?.advance_booking_days]);

  // Debounced auto-save for minimum notice hours.
  useEffect(() => {
    if (!settings) return;
    const n = parseInt(noticeText, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(0, Math.min(168, n));
    if (clamped === settings.minimum_notice_hours) return;
    const id = setTimeout(() => {
      updateSettingsMutation.mutate({ minimum_notice_hours: clamped });
    }, 400);
    return () => clearTimeout(id);
  }, [noticeText, settings?.minimum_notice_hours]);

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <Card>
          <CardHeader className="px-4 sm:px-6 pt-4 pb-4">
            <TabsList>
              <TabsTrigger value="booking" aria-label={t('settings:tabs.booking')}>
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.booking')}</span>
              </TabsTrigger>
              <TabsTrigger value="myProfile" aria-label={t('settings:tabs.myProfile', 'Profile')}>
                <User className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.myProfile', 'Profile')}</span>
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          {/* Booking Settings Tab */}
          <TabsContent value="booking" className="mt-0">
            <CardContent id="booking-settings" className="px-4 sm:px-6 pt-6">
              <div className="divide-y divide-line">
                <SettingsSection
                  title={t('settings:booking.sections.window', 'Booking Window')}
                  description={t('settings:booking.sections.windowHelp', 'How far in advance customers can book and limits per day.')}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('settings:booking.form.advanceBookingDays', 'Advance Booking Days')}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={advanceText}
                        onChange={(e) => setAdvanceText(e.target.value)}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          const clamped = Number.isNaN(n)
                            ? (settings?.advance_booking_days ?? 60)
                            : Math.max(1, Math.min(365, n));
                          setAdvanceText(String(clamped));
                          if (clamped !== settings?.advance_booking_days) {
                            updateSettingsMutation.mutate({ advance_booking_days: clamped });
                          }
                        }}
                      />
                      <p className="text-xs text-soft">
                        {t('staff:settings.advanceDescriptionDynamic', { count: settings?.advance_booking_days ?? 60 })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('settings:booking.form.minimumNoticeHours', 'Minimum Notice (hours)')}</Label>
                      <Input
                        type="number"
                        min={0}
                        max={168}
                        value={noticeText}
                        onChange={(e) => setNoticeText(e.target.value)}
                        onBlur={(e) => {
                          const n = parseInt(e.target.value, 10);
                          const clamped = Number.isNaN(n)
                            ? (settings?.minimum_notice_hours ?? 2)
                            : Math.max(0, Math.min(168, n));
                          setNoticeText(String(clamped));
                          if (clamped !== settings?.minimum_notice_hours) {
                            updateSettingsMutation.mutate({ minimum_notice_hours: clamped });
                          }
                        }}
                      />
                      <p className="text-xs text-soft">
                        {t('staff:settings.noticeDescriptionDynamic', { count: settings?.minimum_notice_hours ?? 0 })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('settings:booking.form.bufferTime', 'Buffer Time (minutes)')}</Label>
                      <Select
                        value={settings?.buffer_minutes?.toString() || '0'}
                        onValueChange={(value) =>
                          updateSettingsMutation.mutate({ buffer_minutes: parseInt(value) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BUFFER_OPTIONS.map((mins) => (
                            <SelectItem key={mins} value={mins.toString()}>
                              {mins === 0 ? t('staff:settings.noBuffer', 'No buffer') : t('staff:settings.minutes', '{{count}} minutes', { count: mins })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-soft">
                        {t('staff:settings.bufferDescriptionDynamic', { count: settings?.buffer_minutes ?? 0 })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('settings:booking.form.slotDuration', 'Time Slot Interval')}</Label>
                      <Select
                        value={settings?.slot_interval_minutes?.toString() || '15'}
                        onValueChange={(value) =>
                          updateSettingsMutation.mutate({ slot_interval_minutes: parseInt(value) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INTERVAL_OPTIONS.map((mins) => (
                            <SelectItem key={mins} value={mins.toString()}>
                              {t('staff:settings.minutes', '{{count}} minutes', { count: mins })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-soft">
                        {t('staff:settings.intervalDescriptionDynamic', { count: settings?.slot_interval_minutes ?? 15 })}
                      </p>
                    </div>
                  </div>
                </SettingsSection>
              </div>

              <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-t flex justify-end z-10">
                <Button
                  variant="outline"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={resetSettingsMutation.isPending}
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('staff:settings.resetDefaults', 'Reset to Defaults')}
                </Button>
              </div>
            </CardContent>
          </TabsContent>

          {/* Personal Profile Tab */}
          <TabsContent value="myProfile" className="mt-0">
            <CardContent className="px-4 sm:px-6 pt-6">
              <ProfileSettingsPanel />
            </CardContent>
          </TabsContent>
        </Card>
      </Tabs>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('staff:settings.resetConfirmTitle', 'Reset booking settings?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'staff:settings.resetConfirmDescription',
                'This will restore all booking settings to their default values. Your current values will be lost.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resetSettingsMutation.mutate();
                setShowResetConfirm(false);
              }}
            >
              {t('staff:settings.resetConfirmAction', 'Reset to defaults')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
